import { supabase } from '../lib/supabase';
import type { EquipmentPreference } from '../types';
export type { EquipmentPreference };

export interface PricingFactors {
  usdToSar: number;
  vatRate: number;
}

export interface SelectedModule {
  id: string;
  manufacturer: string;
  model: string;
  power_stc: number; // W
  voc: number; // V
  isc: number; // A
  vmp: number; // V
  imp: number; // A
  efficiency: number; // %
  warranty_years: number;
  degradation_year1: number; // %
  degradation_annual: number; // %
  cell_type: string;
  bifacial: boolean;
  bifaciality_factor: number | null;
  price_usd: number; // USD/module
}

export interface SelectedInverter {
  id: string;
  manufacturer: string;
  model: string;
  power_ac_nominal: number; // kW
  power_dc_max: number; // kW
  voltage_dc_min: number; // V
  voltage_dc_max: number; // V
  voltage_dc_mpp_min: number; // V
  voltage_dc_mpp_max: number; // V
  current_dc_max: number; // A
  mppt_count: number;
  strings_per_mppt: number;
  voltage_ac_nominal: number; // V
  frequency_ac: number; // Hz
  phases: number; // 1 or 3
  inverter_type: string; // string/central/micro
  protection_rating: string; // IP65, etc.
  cooling_method: string; // natural/forced
  weight: number; // kg
  efficiency_max: number; // %
  efficiency_euro: number; // %
  warranty_years: number;
  price_usd: number; // USD/inverter
}

export type InverterSelectionMode = 'single' | 'multi';

export interface ConstrainedInverterSelection {
  selectedInverter: SelectedInverter | null;
  inverterCount: number;
  selectedTotalAcKw: number;
  selectionMode: InverterSelectionMode;
  acCapKw: number;
}

function medianIndex(len: number): number {
  return Math.floor((len - 1) / 2);
}

function safeNumber(n: unknown, fallback = 0): number {
  const v = typeof n === 'number' ? n : Number(n);
  return Number.isFinite(v) ? v : fallback;
}

function pricePerKw(i: SelectedInverter): number {
  return i.power_ac_nominal > 0 ? i.price_usd / i.power_ac_nominal : Number.POSITIVE_INFINITY;
}

function qualityScore(i: SelectedInverter): number {
  const eff = i.efficiency_euro || i.efficiency_max; // %
  const warranty = i.warranty_years;
  return eff * 2 + warranty * 2 - pricePerKw(i) * 0.2;
}

function medianValue(values: number[]): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  if (sorted.length % 2 === 0) {
    return (sorted[mid - 1] + sorted[mid]) / 2;
  }
  return sorted[mid];
}

function preferenceRankValue(
  inverter: SelectedInverter,
  preference: EquipmentPreference,
  pool: SelectedInverter[]
): number {
  if (preference === 'price') {
    return -pricePerKw(inverter);
  }

  if (preference === 'median') {
    const medianPricePerKw = medianValue(pool.map(pricePerKw).filter(Number.isFinite));
    return -Math.abs(pricePerKw(inverter) - medianPricePerKw);
  }

  return qualityScore(inverter);
}

function pickInverterByPreference(
  candidates: SelectedInverter[],
  preference: EquipmentPreference,
  poolForScoring: SelectedInverter[]
): SelectedInverter | null {
  if (candidates.length === 0) return null;

  if (preference === 'price') {
    return [...candidates].sort((a, b) => pricePerKw(a) - pricePerKw(b))[0] ?? null;
  }

  if (preference === 'median') {
    const sorted = [...candidates].sort((a, b) => pricePerKw(a) - pricePerKw(b));
    return sorted[medianIndex(sorted.length)] ?? null;
  }

  return [...candidates].sort(
    (a, b) => preferenceRankValue(b, preference, poolForScoring) - preferenceRankValue(a, preference, poolForScoring)
  )[0] ?? null;
}

function buildPreferredPool(inverters: SelectedInverter[]): SelectedInverter[] {
  const preferred = inverters.filter((i) => i.inverter_type === 'string');
  return preferred.length > 0 ? preferred : inverters;
}

function groupByNominalAc(inverters: SelectedInverter[]): Map<number, SelectedInverter[]> {
  const grouped = new Map<number, SelectedInverter[]>();
  for (const inverter of inverters) {
    const size = inverter.power_ac_nominal;
    if (!Number.isFinite(size) || size <= 0) continue;
    const existing = grouped.get(size) ?? [];
    existing.push(inverter);
    grouped.set(size, existing);
  }
  return grouped;
}

async function fetchAvailableInverters(): Promise<SelectedInverter[]> {
  const { data, error } = await supabase
    .from('solar_inverters')
    .select('id, manufacturer, model, power_ac_nominal, power_dc_max, voltage_dc_min, voltage_dc_max, voltage_dc_mpp_min, voltage_dc_mpp_max, current_dc_max, mppt_count, strings_per_mppt, efficiency_max, efficiency_euro, voltage_ac_nominal, frequency_ac, phases, inverter_type, protection_rating, cooling_method, weight, warranty_years, price_usd, availability_saudi')
    .eq('availability_saudi', true);

  if (error || !data || (data as any[]).length === 0) return [];

  return (data as any[]).map((inv) => ({
    id: String(inv.id),
    manufacturer: String(inv.manufacturer),
    model: String(inv.model),
    power_ac_nominal: safeNumber(inv.power_ac_nominal),
    power_dc_max: safeNumber(inv.power_dc_max),
    voltage_dc_min: safeNumber(inv.voltage_dc_min),
    voltage_dc_max: safeNumber(inv.voltage_dc_max),
    voltage_dc_mpp_min: safeNumber(inv.voltage_dc_mpp_min),
    voltage_dc_mpp_max: safeNumber(inv.voltage_dc_mpp_max),
    current_dc_max: safeNumber(inv.current_dc_max),
    mppt_count: safeNumber(inv.mppt_count),
    strings_per_mppt: safeNumber(inv.strings_per_mppt),
    phases: safeNumber(inv.phases),
    inverter_type: String(inv.inverter_type),
    protection_rating: String(inv.protection_rating),
    cooling_method: String(inv.cooling_method),
    weight: safeNumber(inv.weight),
    efficiency_max: safeNumber(inv.efficiency_max),
    efficiency_euro: safeNumber(inv.efficiency_euro),
    voltage_ac_nominal: safeNumber(inv.voltage_ac_nominal),
    frequency_ac: safeNumber(inv.frequency_ac),
    warranty_years: safeNumber(inv.warranty_years),
    price_usd: safeNumber(inv.price_usd),
  })) as SelectedInverter[];
}

function chooseSingleInverterAtOrBelowFromPool(
  allInverters: SelectedInverter[],
  preference: EquipmentPreference,
  maxAcKw: number
): ConstrainedInverterSelection {
  const acCapKw = Number.isFinite(maxAcKw) && maxAcKw > 0 ? maxAcKw : 0;
  const pool = buildPreferredPool(allInverters);
  const bySize = groupByNominalAc(pool);
  const sizes = [...bySize.keys()].sort((a, b) => a - b);
  if (sizes.length === 0) {
    return {
      selectedInverter: null,
      inverterCount: 0,
      selectedTotalAcKw: 0,
      selectionMode: 'single',
      acCapKw,
    };
  }

  const eligibleSizes = sizes.filter((size) => size <= acCapKw);
  const chosenSize = eligibleSizes.length > 0
    ? eligibleSizes[eligibleSizes.length - 1]
    : sizes[0];
  const selectedInverter = pickInverterByPreference(bySize.get(chosenSize) ?? [], preference, pool);
  const selectedUnitKw = selectedInverter?.power_ac_nominal ?? chosenSize;

  return {
    selectedInverter,
    inverterCount: 1,
    selectedTotalAcKw: selectedUnitKw,
    selectionMode: 'single',
    acCapKw,
  };
}

function chooseMultiInverterPackAtOrBelowFromPool(
  allInverters: SelectedInverter[],
  preference: EquipmentPreference,
  maxAcKw: number
): ConstrainedInverterSelection {
  const acCapKw = Number.isFinite(maxAcKw) && maxAcKw > 0 ? maxAcKw : 0;
  const pool = buildPreferredPool(allInverters);
  const bySize = groupByNominalAc(pool);
  const sizes = [...bySize.keys()].sort((a, b) => a - b);
  if (sizes.length === 0) {
    return {
      selectedInverter: null,
      inverterCount: 0,
      selectedTotalAcKw: 0,
      selectionMode: 'multi',
      acCapKw,
    };
  }

  type Candidate = {
    selectedInverter: SelectedInverter;
    inverterCount: number;
    selectedTotalAcKw: number;
    preferenceRank: number;
  };

  const candidates: Candidate[] = [];
  for (const size of sizes) {
    if (size > acCapKw) continue;
    const inverterCount = Math.floor(acCapKw / size);
    if (inverterCount < 1) continue;
    const selectedInverter = pickInverterByPreference(bySize.get(size) ?? [], preference, pool);
    if (!selectedInverter) continue;
    candidates.push({
      selectedInverter,
      inverterCount,
      selectedTotalAcKw: size * inverterCount,
      preferenceRank: preferenceRankValue(selectedInverter, preference, pool),
    });
  }

  if (candidates.length === 0) {
    const smallestSize = sizes[0];
    const selectedInverter = pickInverterByPreference(bySize.get(smallestSize) ?? [], preference, pool);
    const selectedUnitKw = selectedInverter?.power_ac_nominal ?? smallestSize;
    return {
      selectedInverter,
      inverterCount: selectedInverter ? 1 : 0,
      selectedTotalAcKw: selectedInverter ? selectedUnitKw : 0,
      selectionMode: 'multi',
      acCapKw,
    };
  }

  candidates.sort((a, b) => {
    if (b.selectedTotalAcKw !== a.selectedTotalAcKw) return b.selectedTotalAcKw - a.selectedTotalAcKw;
    if (a.inverterCount !== b.inverterCount) return a.inverterCount - b.inverterCount;
    return b.preferenceRank - a.preferenceRank;
  });

  const best = candidates[0];
  return {
    selectedInverter: best.selectedInverter,
    inverterCount: best.inverterCount,
    selectedTotalAcKw: best.selectedTotalAcKw,
    selectionMode: 'multi',
    acCapKw,
  };
}

export async function fetchPricingFactors(): Promise<PricingFactors> {
  // Defaults if table is missing/unreachable
  const defaults: PricingFactors = { usdToSar: 3.75, vatRate: 0.15 };

  const { data, error } = await supabase
    .from('saudi_pricing_factors')
    .select('factor_name, factor_value')
    .in('factor_name', ['usd_to_sar', 'vat_rate']);

  if (error || !data) return defaults;

  const map = new Map<string, number>();
  for (const row of data as any[]) {
    map.set(String(row.factor_name), safeNumber(row.factor_value));
  }

  const usdToSar = map.get('usd_to_sar') || defaults.usdToSar;
  const vatRatePercent = map.get('vat_rate');
  const vatRate = vatRatePercent != null && vatRatePercent > 1 ? vatRatePercent / 100 : (vatRatePercent ?? defaults.vatRate);

  return { usdToSar, vatRate };
}

export async function selectModule(preference: EquipmentPreference): Promise<SelectedModule | null> {
  const { data, error } = await supabase
    .from('solar_modules')
    .select('id, manufacturer, model, power_stc, voc, isc, vmp, imp, efficiency, warranty_years, degradation_year1, degradation_annual, cell_type, bifacial, bifaciality_factor, price_usd, availability_saudi')
    .eq('availability_saudi', true);

  if (error || !data || (data as any[]).length === 0) return null;

  const modules = (data as any[]).map((m) => ({
    id: String(m.id),
    manufacturer: String(m.manufacturer),
    model: String(m.model),
    power_stc: safeNumber(m.power_stc),
    voc: safeNumber(m.voc),
    isc: safeNumber(m.isc),
    vmp: safeNumber(m.vmp),
    imp: safeNumber(m.imp),
    efficiency: safeNumber(m.efficiency),
    warranty_years: safeNumber(m.warranty_years),
    degradation_year1: safeNumber(m.degradation_year1),
    degradation_annual: safeNumber(m.degradation_annual),
    cell_type: String(m.cell_type),
    bifacial: Boolean(m.bifacial),
    bifaciality_factor: m.bifaciality_factor == null ? null : safeNumber(m.bifaciality_factor),
    price_usd: safeNumber(m.price_usd),
  })) as SelectedModule[];

  const pricePerW = (m: SelectedModule) => (m.power_stc > 0 ? m.price_usd / m.power_stc : Number.POSITIVE_INFINITY);

  if (preference === 'price') {
    return modules.sort((a, b) => pricePerW(a) - pricePerW(b))[0] ?? null;
  }

  if (preference === 'median') {
    const sorted = modules.sort((a, b) => pricePerW(a) - pricePerW(b));
    return sorted[medianIndex(sorted.length)] ?? null;
  }

  // quality
  const score = (m: SelectedModule) => {
    // Heuristic: prioritize efficiency and warranty, penalize degradation; small bonus for bifacial
    const eff = m.efficiency; // %
    const warranty = m.warranty_years;
    const deg = m.degradation_annual; // %
    const bifacialBonus = m.bifacial ? 1.5 : 0;
    return eff * 1.5 + warranty * 1.2 - deg * 4 + bifacialBonus;
  };
  return modules.sort((a, b) => score(b) - score(a))[0] ?? null;
}

export async function selectInverter(preference: EquipmentPreference, desiredUnitKw: number): Promise<SelectedInverter | null> {
  const inverters = await fetchAvailableInverters();
  if (inverters.length === 0) return null;

  // Prefer "string" in most cases; keep central as fallback.
  const pool = buildPreferredPool(inverters);

  // Narrow to near desired size if possible
  const band = pool.filter((i) => i.power_ac_nominal >= desiredUnitKw * 0.6 && i.power_ac_nominal <= desiredUnitKw * 1.6);
  const candidates = band.length > 0 ? band : pool;

  return pickInverterByPreference(candidates, preference, pool);
}

export async function selectSingleInverterAtOrBelow(
  preference: EquipmentPreference,
  maxAcKw: number
): Promise<ConstrainedInverterSelection> {
  const inverters = await fetchAvailableInverters();
  return chooseSingleInverterAtOrBelowFromPool(inverters, preference, maxAcKw);
}

export async function selectMultiInverterPackAtOrBelow(
  preference: EquipmentPreference,
  maxAcKw: number
): Promise<ConstrainedInverterSelection> {
  const inverters = await fetchAvailableInverters();
  return chooseMultiInverterPackAtOrBelowFromPool(inverters, preference, maxAcKw);
}

export function suggestInverterUnitSizeKw(requiredAcKw: number): number {
  // Simple heuristic buckets
  if (requiredAcKw <= 7) return 5;
  if (requiredAcKw <= 15) return 10;
  if (requiredAcKw <= 30) return 20;
  if (requiredAcKw <= 80) return 50;
  if (requiredAcKw <= 160) return 100;
  return 185;
}


